import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { categorizeDomain, PUBLIC_DOMAIN_CATEGORIES, PublicDomainCategory } from './categories'

import { getPublicDomains } from '@/api/internal'
import Select from '@/components/Select'
import DomainListTable, { DomainRow } from '@/pages/app/DomainManagement/components/DomainListTable'

export const PUBLIC_DOMAINS_QUERY_KEY = ['publicDomains'] as const

const ALL_CATEGORIES = 'all'

const PILL_CLASS: Record<PublicDomainCategory, string> = {
  'Top Provider': 'pillTopProvider',
  'Education (.edu)': 'pillEducation',
  'Business / Shared': 'pillBusiness',
  'Privacy-Focused': 'pillPrivacy',
  Other: 'pillOther',
}

const PublicDomainList = () => {
  const [category, setCategory] = useState(ALL_CATEGORIES)

  const { data, isLoading, error } = useQuery({
    queryKey: PUBLIC_DOMAINS_QUERY_KEY,
    queryFn: async () => (await getPublicDomains()).data,
  })

  const categorized = useMemo(
    () =>
      (data?.domains ?? []).map((domain) => ({
        domain,
        category: categorizeDomain(domain),
      })),
    [data]
  )

  const categoryCounts = useMemo(() => {
    const counts = new Map<PublicDomainCategory, number>()
    for (const row of categorized) {
      counts.set(row.category, (counts.get(row.category) ?? 0) + 1)
    }
    return counts
  }, [categorized])

  const rows: DomainRow[] = useMemo(
    () =>
      categorized
        .filter((row) => category === ALL_CATEGORIES || row.category === category)
        .map((row) => ({
          domain: row.domain,
          pill: { label: row.category, classKey: PILL_CLASS[row.category] },
        })),
    [categorized, category]
  )

  const categoryOptions = [
    {
      value: ALL_CATEGORIES,
      label: `All categories (${categorized.length.toLocaleString()})`,
    },
    ...PUBLIC_DOMAIN_CATEGORIES.map((cat) => ({
      value: cat,
      label: `${cat} (${(categoryCounts.get(cat) ?? 0).toLocaleString()})`,
    })),
  ]

  return (
    <DomainListTable
      rows={rows}
      isLoading={isLoading}
      hasError={!!error}
      listName="public domains"
      banner={
        <>
          Domains belonging to free/shared email providers (Gmail, Outlook, Yahoo, etc.) that can&apos;t be claimed by a
          single org because many unrelated users share them. This list is read-only — it&apos;s maintained in the
          backend&apos;s domain blacklist config.
        </>
      }
      totalChip={`${(data?.total ?? 0).toLocaleString()} domains`}
      controls={<Select value={category} options={categoryOptions} onChange={setCategory} />}
      pillHeader="Category"
      resetPageKey={category}
    />
  )
}

export default PublicDomainList
